/**
 * Change WAN Modal
 *
 * Troca o endereçamento da interface WAN de um gateway já criado.
 *
 * Existe porque, até então, um gateway que subiu com DHCP numa rede sem
 * servidor DHCP só se consertava com rebuild (destrói e recria a VM). Aqui a
 * VM é preservada: o backend reescreve o `ipconfig0` e reinicia, e o cloud-init
 * reaplica a rede no boot.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangleIcon, GlobeIcon, Loader2Icon, XIcon } from 'lucide-react'
import { useState } from 'react'

import { changeGatewayWan, getGatewayWanAvailableIPs } from '@nebula/shared/services'
import type { CloudGateway, ChangeGatewayWanRequest, GatewayWanMode } from '@nebula/shared/services'
import { getErrorMessage } from '@nebula/shared/utils'

type ChangeWanModalProps = {
  gateway: CloudGateway
  onClose: () => void
  onChanged: () => void
}

export default function ChangeWanModal({ gateway, onClose, onChanged }: ChangeWanModalProps) {
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<GatewayWanMode>(gateway.wan.mode ?? 'dhcp')
  const [publicIpId, setPublicIpId] = useState<string>(gateway.wan.public_ip_id ?? '')
  const [ip, setIp] = useState(gateway.wan.ip ?? '')
  const [gw, setGw] = useState(gateway.wan.gateway ?? '')
  const [netmask, setNetmask] = useState(gateway.wan.netmask ?? '255.255.255.0')
  const [dns, setDns] = useState((gateway.wan.dns ?? []).join(', '))

  // Sempre habilitado (não só no modo 'pool'): a resposta traz `dhcp_available`,
  // e é ela que decide se o rádio DHCP pode ser oferecido. Buscar só no modo
  // 'pool' deixaria o DHCP habilitado num cluster que não o fornece — o cliente
  // só descobriria no 400 do backend.
  const {
    data: ipsData,
    isLoading: loadingIps,
    error: ipsError,
  } = useQuery({
    queryKey: ['gateway-wan-ips', gateway.cluster_id],
    queryFn: () => getGatewayWanAvailableIPs(gateway.cluster_id),
  })
  // O backend distingue "sem VPC neste cluster" de "sem IP no pool"; sem exibir
  // a mensagem dele, o cliente abriria chamado pedindo IPs que existem.
  const ipsErrorMsg = ipsError ? getErrorMessage(ipsError) : null
  const ips = ipsData?.ips ?? []
  // Enquanto carrega, assume disponível para não piscar "indisponível" na tela.
  const dhcpAvailable = ipsData?.dhcp_available ?? true

  const mutation = useMutation({
    mutationFn: (data: ChangeGatewayWanRequest) => changeGatewayWan(gateway.id, data),
    onSuccess: () => onChanged(),
    onError: (err: any) => {
      setError(getErrorMessage(err))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (mode === 'static') {
      if (!ip || !gw) {
        setError('IP e Gateway são obrigatórios no endereçamento fixo')
        return
      }
      // Sem máscara não dá para derivar o tamanho do bloco — /24 presumido num
      // /25 deixa o gateway sem alcançar o next-hop.
      if (!netmask) {
        setError('Máscara de rede é obrigatória no endereçamento fixo')
        return
      }
    }

    const dnsList = dns
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean)

    mutation.mutate({
      wan_mode: mode,
      wan_public_ip_id: mode === 'pool' ? publicIpId || null : null,
      wan_ip: mode === 'static' ? ip : null,
      wan_gateway: mode === 'static' ? gw : null,
      wan_netmask: mode === 'static' ? netmask : null,
      // Só no modo 'static' (nos outros o campo nem aparece, e reenviar o valor
      // herdado reinstalaria o resolver do pool que o backend acabou de limpar).
      // Lista vazia é enviada como `[]`, não `null`: `null` significa "não mexa"
      // no backend, e aí não haveria como apagar o DNS.
      wan_dns: mode === 'static' ? dnsList : null,
    })
  }

  const temVM = gateway.has_vm

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mobile-modal-surface flex w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <GlobeIcon className="size-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Alterar WAN</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto p-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {temVM && (
            <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
              <span>
                O gateway será <strong>reiniciado</strong> para aplicar o novo endereçamento. A
                conexão dos seus recursos cai por alguns segundos.
              </span>
            </div>
          )}

          {gateway.wan.mode === 'pool' && mode !== 'pool' && (
            <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
              <span>
                O IP público <strong>{gateway.wan.ip}</strong> será devolvido ao provedor. Se voltar
                a usar um IP do pool, o endereço pode ser outro — port-forwards e DNS externo
                apontando para ele param de funcionar.
              </span>
            </div>
          )}

          <div className="space-y-2">
            <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
              <input
                type="radio"
                name="mode"
                checked={mode === 'dhcp'}
                disabled={!dhcpAvailable}
                onChange={() => setMode('dhcp')}
                className="mt-0.5 size-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">
                Obter IP automaticamente (DHCP)
                {!dhcpAvailable && (
                  <span className="mt-0.5 block text-xs text-amber-700">
                    Indisponível: a rede do provedor neste cluster não fornece DHCP
                  </span>
                )}
              </span>
            </label>

            <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3">
              <input
                type="radio"
                name="mode"
                checked={mode === 'pool'}
                onChange={() => setMode('pool')}
                className="mt-0.5 size-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">Usar um IP público do meu provedor</span>
            </label>

            <label className="flex cursor-pointer items-start space-x-3 rounded-lg bg-gray-50 p-3">
              <input
                type="radio"
                name="mode"
                checked={mode === 'static'}
                onChange={() => setMode('static')}
                className="mt-0.5 size-4 text-purple-600"
              />
              <span className="text-sm text-gray-700">Informar endereçamento manualmente</span>
            </label>
          </div>

          {mode === 'pool' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">IP público</label>
              {loadingIps ? (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Loader2Icon className="size-4 animate-spin" />
                  <span>Carregando IPs disponíveis...</span>
                </div>
              ) : ipsErrorMsg ? (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  {ipsErrorMsg}
                </div>
              ) : ips.length === 0 ? (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  Nenhum IP público disponível neste cluster. Solicite ao seu provedor o cadastro de
                  novos IPs.
                </div>
              ) : (
                <select
                  value={publicIpId}
                  onChange={(e) => setPublicIpId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Escolher automaticamente</option>
                  {ips.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.ip_address}
                      {item.owned ? ' (já é seu)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {mode === 'static' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">IP *</label>
                <input
                  type="text"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder="203.0.113.10"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Gateway *</label>
                <input
                  type="text"
                  value={gw}
                  onChange={(e) => setGw(e.target.value)}
                  placeholder="203.0.113.1"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Máscara de rede *
                </label>
                <input
                  type="text"
                  value={netmask}
                  onChange={(e) => setNetmask(e.target.value)}
                  placeholder="255.255.255.0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Servidores DNS
                </label>
                <input
                  type="text"
                  value={dns}
                  onChange={(e) => setDns(e.target.value)}
                  placeholder="8.8.8.8, 8.8.4.4"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-sm text-white transition-colors hover:bg-purple-700 disabled:opacity-60"
            >
              {mutation.isPending && <Loader2Icon className="size-4 animate-spin" />}
              <span>{temVM ? 'Aplicar e reiniciar' : 'Aplicar'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
